import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Download,
  MessageCircle,
  MoreHorizontal,
  Pencil,
  Phone,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Filter,
  Check,
  Users,
  X,
} from "lucide-react";
import AddLeadModal from "./addlead";
import { getAccountManagerLeads } from "../api/auth";
import api from "../api/api";
import Toast, { useToast } from "../components/Toast";
import ConfirmDialog, { useConfirm } from "./ConfirmDialog";

// API Endpoint binding using your centralized api configuration

// Formats any incoming date value (ISO string, or the RFC1123-style
// "Thu, 02 Jul 2026 00:00:00 GMT" string Flask's default JSON encoder
// produces for date/datetime objects) into a plain "02 Jul 2026" display.
const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getStatusClasses = (status) => {
  const currentStatus = String(status || "").toLowerCase();
  if (currentStatus === "ongoing") {
    return "bg-blue-100 text-blue-700";
  }
  if (currentStatus === "won" || currentStatus === "won") {
    return "bg-green-100 text-green-700";
  }
  return "bg-yellow-100 text-yellow-700";
};

// Formats a numeric amount using the Indian digit-grouping system (e.g. 1234567 -> "12,34,567")
const formatIndianCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const sanitized = String(value).replace(/[^0-9.]/g, "");
  if (sanitized === "") return "-";

  const [integerPartRaw, ...decimalParts] = sanitized.split(".");
  const decimalPart = decimalParts.length ? decimalParts.join("") : undefined;
  const integerPart = integerPartRaw.replace(/^0+(?=\d)/, "") || "0";

  let formattedInteger;
  if (integerPart.length <= 3) {
    formattedInteger = integerPart;
  } else {
    const lastThree = integerPart.slice(-3);
    const remaining = integerPart.slice(0, -3);
    formattedInteger = remaining.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree;
  }

  return decimalPart !== undefined ? `${formattedInteger}` : formattedInteger;
};


// Converts an array of row objects into a downloadable CSV file using the
// given column definitions, then triggers the browser's save dialog.
const exportRowsToCSV = (rows, columns, filename) => {
  const escapeCsvValue = (value) => {
    const str = String(value ?? "");
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const headerLine = columns.map((col) => escapeCsvValue(col.label)).join(",");
  const bodyLines = rows.map((row) =>
    columns.map((col) => escapeCsvValue(col.value(row))).join(",")
  );
  const csvContent = [headerLine, ...bodyLines].join("\n");

  const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ==========================================================
// MULTI-SELECT AUTOCOMPLETE FILTER FIELD
// Shared by Customer Name and Account Manager filters: type to search a
// dropdown of matching values, click one to add it as a chip, keep typing
// to add more. Selected values are combined with OR (any lead matching
// any selected chip passes).
// ==========================================================
function MultiSelectFilterField({ label, placeholder, options, selectedValues, onChange, onClear }) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const filteredOptions = options.filter(
    (opt) => !selectedValues.includes(opt) && opt.toLowerCase().includes(search.toLowerCase())
  );

  const addValue = (value) => {
    onChange([...selectedValues, value]);
    setSearch("");
  };

  const removeValue = (value) => {
    onChange(selectedValues.filter((v) => v !== value));
  };

  return (
    <div className="mb-3">
      <div className="mb-1.5 flex items-center justify-between">
        <p className="text-xs font-bold text-slate-600">{label}</p>
        {onClear && selectedValues.length > 0 && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClear}
            className="text-[11px] font-semibold text-blue-600 hover:text-blue-700 cursor-pointer"
          >
            Clear
          </button>
        )}
      </div>

      {selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-1.5">
          {selectedValues.map((value) => (
            <span
              key={value}
              className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100 pl-2 pr-1 py-0.5 text-[11px] font-medium max-w-full"
            >
              <span className="truncate">{value}</span>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => removeValue(value)}
                className="shrink-0 text-blue-400 hover:text-blue-700 cursor-pointer"
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <input
          type="text"
          placeholder={placeholder}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setIsOpen(false)}
          className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        {isOpen && filteredOptions.length > 0 && (
          <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-20 max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg py-1">
            {filteredOptions.map((opt) => (
              <div
                key={opt}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addValue(opt)}
                className="px-2.5 py-1.5 text-sm text-slate-700 hover:bg-blue-50 cursor-pointer truncate"
              >
                {opt}
              </div>
            ))}
          </div>
        )}

        {isOpen && search && filteredOptions.length === 0 && (
          <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-20 rounded-lg border border-slate-200 bg-white shadow-lg py-2 px-2.5 text-xs text-slate-400">
            No matches
          </div>
        )}
      </div>
    </div>
  );
}

// OPF status shown in the "OPF Raised" column and used by the quick filters.
// "Not Raised" -> no OPF yet, "Draft" -> being filled in, "Requested" -> sent
// for approval with no decision yet, "Approved" / "Declined" -> decision
// taken, "Submitted" -> OPF raised.
const OPF_STATUSES = ["Not Raised", "Draft", "Requested", "Approved", "Declined", "Submitted"];

const OPF_STATUS_CLASSES = {
  "Not Raised": "bg-slate-100 text-slate-600",
  Draft: "bg-yellow-100 text-yellow-700",
  Requested: "bg-blue-100 text-blue-700",
  Approved: "bg-teal-100 text-teal-700",
  Declined: "bg-red-100 text-red-600",
  Submitted: "bg-green-100 text-green-600",
};

// "Raised" is the old name for "Submitted" - mapped here so the column stays
// correct even if an older API response is still being served.
const getOpfStatus = (lead) => {
  const value = lead?.opf_raised;
  if (!value) return "Not Raised";
  return value === "Raised" ? "Submitted" : value;
};

export default function Leads() {
  const [leadsData, setLeadsData] = useState([]); // Dynamic state holding the real API data
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { toast, showToast, hideToast } = useToast();
  const { confirm, confirmState, handleConfirm, handleCancel } = useConfirm();
  const [open, setOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState("All");
  const [activeDropdownId, setActiveDropdownId] = useState(null);
  const [stageOptions, setStageOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [stageFilter, setStageFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [customerNameFilter, setCustomerNameFilter] = useState([]);
  const [presalesFilter, setPresalesFilter] = useState([]);
  const [bidManagerFilter, setBidManagerFilter] = useState([]);
  const [stateFilter, setStateFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [accountManagerFilter, setAccountManagerFilter] = useState([]);
  const [oemFilter, setOemFilter] = useState("");
  const [oemAcMgrFilter, setOemAcMgrFilter] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");
  const [expectedClosureFrom, setExpectedClosureFrom] = useState("");
  const [expectedClosureTo, setExpectedClosureTo] = useState("");
  const [drNumberFilter, setDrNumberFilter] = useState("");
  const [drExpiryFrom, setDrExpiryFrom] = useState("");
  const [drExpiryTo, setDrExpiryTo] = useState("");
  const [remarksFilter, setRemarksFilter] = useState("");
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const [amMenuOpen, setAmMenuOpen] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  // Sorting & Pagination
  const [sortField, setSortField] = useState("id");
  const [sortOrder, setSortOrder] = useState("asc"); // "asc" | "desc"
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1);
    setSelected([]);
  };

  const NUMERIC_SORT_FIELDS = ["id", "amount", "expectedClosure", "drExpiry"];

  const getSortValue = (lead, field) => {
    switch (field) {
      case "id":
        return Number(lead.ld_id ?? lead.id) || 0;
      case "customerName":
        return lead.customerName || lead.cust_name || "";
      case "accountManager":
        return lead.account_manager || "";
      case "presales":
        return lead.presales || "";
      case "bid_manager":
        return lead.bid_manager || "";
      case "opfRaised":
        return getOpfStatus(lead);
      case "oem":
        return lead.oem || lead.ld_oem || "";
      case "oemAcMgr":
        return lead.oemAcMgr || lead.ld_oem_ac_mgr || "";
      case "product":
        return lead.product || lead.ld_product || "";
      case "level":
        return lead.level || lead.ld_level_id || "";
      case "status":
        return lead.status || "";
      case "amount": {
        const raw = String(lead.estimatedBudget ?? lead.ld_estimated_budget ?? "").replace(/[^0-9.]/g, "");
        return Number(raw) || 0;
      }
      case "expectedClosure": {
        const parsed = new Date(lead.expectedClosure || lead.ld_expected_closure || 0);
        return isNaN(parsed.getTime()) ? 0 : parsed.getTime();
      }
      case "drNumber":
        return lead.drNumber || lead.ld_dr_number || "";
      case "drExpiry": {
        const parsed = new Date(lead.drExpiry || lead.ld_dr_expiry || 0);
        return isNaN(parsed.getTime()) ? 0 : parsed.getTime();
      }
      case "remarks":
        return lead.statusRemark || lead.ld_remarks || "";
      default:
        return "";
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const filterMenuRef = useRef(null);
  const amMenuRef = useRef(null);

  // ==========================================
  // ADMIN SCOPING: admins see a blank list until an Account Manager is
  // picked (either via the toolbar dropdown, the filter panel, or by
  // arriving here from a specific user's dashboard). "View All" opts out
  // of that restriction and shows everyone's opportunities at once.
  // ==========================================
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isAdmin = (currentUser.department || "").toLowerCase() === "admin";
  const [viewAll, setViewAll] = useState(false);

  // Arriving from a specific user's dashboard (Users list -> that user's
  // dashboard -> a stat card) pre-selects that person as the Account
  // Manager instead of showing a blank page.
  useEffect(() => {
    if (location.state?.accountManager) {
      setAccountManagerFilter([location.state.accountManager]);
      setViewAll(false);
    } else if (location.state?.viewAll) {
      setAccountManagerFilter([]);
      setViewAll(true);
    }
  }, [location.state]);

  // ==========================================
  // UNIQUE OPTION LISTS FOR THE MULTI-SELECT FILTER DROPDOWNS
  // Derived from whatever data is currently loaded, so suggestions always
  // reflect real values instead of a hardcoded/stale list.
  // ==========================================
  const customerNameOptions = useMemo(
    () =>
      Array.from(new Set(leadsData.map((l) => l.customerName || l.cust_name).filter(Boolean))).sort(),
    [leadsData]
  );
  const accountManagerOptions = useMemo(
    () => Array.from(new Set(leadsData.map((l) => l.account_manager).filter(Boolean))).sort(),
    [leadsData]
  );
  const presalesOptions = useMemo(
    () => Array.from(new Set(leadsData.map((l) => l.presales).filter(Boolean))).sort(),
    [leadsData]
  );
  const bidManagerOptions = useMemo(
    () => Array.from(new Set(leadsData.map((l) => l.bid_manager).filter(Boolean))).sort(),
    [leadsData]
  );

  // ==========================================
  // FETCH ASSIGNED LEADS FROM FLASK API
  // ==========================================
  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await getAccountManagerLeads();
      if (response.data && response.data.success) {
        console.log(response.data.data);
        setLeadsData(response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching lead allocations:", error);
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // FETCH STAGE / STATUS DROPDOWN OPTIONS
  // ==========================================
  const fetchDropdownOptions = async () => {
    try {
      const response = await api.get("/leads/meta-options");
      if (response.data?.success) {
        const meta = response.data.data || {};
        setStageOptions(meta.levels || []);
        setStatusOptions(meta.statuses || []);
      }
    } catch (error) {
      console.error("Error fetching stage/status options:", error);
    }
  };

  useEffect(() => {
    fetchLeads();
    fetchDropdownOptions();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        activeDropdownId !== null &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setActiveDropdownId(null);
      }
      if (
        filterMenuOpen &&
        filterMenuRef.current &&
        !filterMenuRef.current.contains(event.target)
      ) {
        setFilterMenuOpen(false);
      }
      if (
        amMenuOpen &&
        amMenuRef.current &&
        !amMenuRef.current.contains(event.target)
      ) {
        setAmMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeDropdownId, filterMenuOpen, amMenuOpen]);

  // ==========================================
  // FIXED FILTER LAYER: RESOLVES BLANK ROWS
  // ==========================================
  const isAdminBlankState = isAdmin && !viewAll && accountManagerFilter.length === 0;

  const filtered = (isAdminBlankState ? [] : leadsData).filter((lead) => {
    // 1. Safe mapping for both API structured keys and raw database snake_case columns
    const customerName = lead.customerName || lead.cust_name || ""; 
    const oppId = lead.ld_id ?? lead.id ?? "";
    const oppIdDisplay = oppId !== "" ? `LD-${String(oppId).padStart(4, "0")}` : "";
    const accountManager = lead.account_manager || "";
    const accountManagerEmail = lead.account_manager_email || "";
    const presales = lead.presales || "";
    const searchTerm = search.toLowerCase();
    const matchesSearch =
      customerName.toLowerCase().includes(searchTerm) ||
      String(oppId).toLowerCase().includes(searchTerm) ||
      oppIdDisplay.toLowerCase().includes(searchTerm) ||
      accountManager.toLowerCase().includes(searchTerm) ||
      accountManagerEmail.toLowerCase().includes(searchTerm) ||
      presales.toLowerCase().includes(searchTerm);

    // 2. OPF status pipeline matching
    let matchesFilter = true;
    if (activeFilter !== "All") {
      matchesFilter = getOpfStatus(lead) === activeFilter;
    }

    // 3. Stage / Status multi-select filters from the filter icon menu
    const leadStage = lead.level || lead.ld_level_id || "";
    const matchesStage = stageFilter.length === 0 || stageFilter.includes(leadStage);

    const leadStatus = lead.status || "";
    const matchesStatus = statusFilter.length === 0 || statusFilter.includes(leadStatus);

    // 4. Every other column, filterable from the filter icon menu
    const matchesCustomerNameFilter =
      customerNameFilter.length === 0 || customerNameFilter.includes(customerName);

    const matchesPresalesFilter =
      presalesFilter.length === 0 || presalesFilter.includes(presales);

    const leadBidManager = lead.bid_manager || "";
    const matchesBidManagerFilter =
      bidManagerFilter.length === 0 || bidManagerFilter.includes(leadBidManager);

    const leadState = lead.state || lead.cust_state || "";
    const matchesStateFilter = !stateFilter || leadState.toLowerCase().includes(stateFilter.toLowerCase());

    const leadCity = lead.city || lead.cust_city || "";
    const matchesCityFilter = !cityFilter || leadCity.toLowerCase().includes(cityFilter.toLowerCase());

    const matchesAccountManagerFilter =
      accountManagerFilter.length === 0 || accountManagerFilter.includes(accountManager);

    const leadOem = lead.oem || lead.ld_oem || "";
    const matchesOemFilter = !oemFilter || leadOem.toLowerCase().includes(oemFilter.toLowerCase());

    const leadOemAcMgr = lead.oemAcMgr || lead.ld_oem_ac_mgr || "";
    const matchesOemAcMgrFilter =
      !oemAcMgrFilter || leadOemAcMgr.toLowerCase().includes(oemAcMgrFilter.toLowerCase());

    const leadProduct = lead.product || lead.ld_product || "";
    const matchesProductFilter = !productFilter || leadProduct.toLowerCase().includes(productFilter.toLowerCase());

    const leadAmount = Number(
      String(lead.estimatedBudget ?? lead.ld_estimated_budget ?? "").replace(/[^0-9.]/g, "")
    ) || 0;
    const matchesAmountMin = amountMin === "" || leadAmount >= Number(amountMin);
    const matchesAmountMax = amountMax === "" || leadAmount <= Number(amountMax);

    const leadExpectedClosure = new Date(lead.expectedClosure || lead.ld_expected_closure || 0);
    const matchesExpectedClosureFrom =
      !expectedClosureFrom ||
      (!isNaN(leadExpectedClosure.getTime()) && leadExpectedClosure >= new Date(expectedClosureFrom));
    const matchesExpectedClosureTo =
      !expectedClosureTo ||
      (!isNaN(leadExpectedClosure.getTime()) && leadExpectedClosure <= new Date(`${expectedClosureTo}T23:59:59`));

    const leadDrNumber = lead.drNumber || lead.ld_dr_number || "";
    const matchesDrNumberFilter =
      !drNumberFilter || leadDrNumber.toLowerCase().includes(drNumberFilter.toLowerCase());

    const leadDrExpiry = new Date(lead.drExpiry || lead.ld_dr_expiry || 0);
    const matchesDrExpiryFrom =
      !drExpiryFrom || (!isNaN(leadDrExpiry.getTime()) && leadDrExpiry >= new Date(drExpiryFrom));
    const matchesDrExpiryTo =
      !drExpiryTo || (!isNaN(leadDrExpiry.getTime()) && leadDrExpiry <= new Date(`${drExpiryTo}T23:59:59`));

    const leadRemarks = lead.statusRemark || lead.ld_remarks || "";
    const matchesRemarksFilter =
      !remarksFilter || leadRemarks.toLowerCase().includes(remarksFilter.toLowerCase());

    return (
      matchesSearch &&
      matchesFilter &&
      matchesStage &&
      matchesStatus &&
      matchesCustomerNameFilter &&
      matchesPresalesFilter &&
      matchesBidManagerFilter &&
      matchesStateFilter &&
      matchesCityFilter &&
      matchesAccountManagerFilter &&
      matchesOemFilter &&
      matchesOemAcMgrFilter &&
      matchesProductFilter &&
      matchesAmountMin &&
      matchesAmountMax &&
      matchesExpectedClosureFrom &&
      matchesExpectedClosureTo &&
      matchesDrNumberFilter &&
      matchesDrExpiryFrom &&
      matchesDrExpiryTo &&
      matchesRemarksFilter
    );
  }).sort((a, b) => {
    const valA = getSortValue(a, sortField);
    const valB = getSortValue(b, sortField);

    if (NUMERIC_SORT_FIELDS.includes(sortField)) {
      return sortOrder === "asc" ? valA - valB : valB - valA;
    }

    const strA = String(valA).toLowerCase();
    const strB = String(valB).toLowerCase();
    if (strA < strB) return sortOrder === "asc" ? -1 : 1;
    if (strA > strB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const selectedLeads = selected.map((index) => filtered[index]);

  /* PAGINATION (based on sorted + filtered results) */
  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paginatedLeads = filtered
    .map((lead, index) => ({ lead, index }))
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    search,
    activeFilter,
    stageFilter,
    statusFilter,
    customerNameFilter,
    presalesFilter,
    bidManagerFilter,
    stateFilter,
    cityFilter,
    accountManagerFilter,
    oemFilter,
    oemAcMgrFilter,
    productFilter,
    amountMin,
    amountMax,
    expectedClosureFrom,
    expectedClosureTo,
    drNumberFilter,
    drExpiryFrom,
    drExpiryTo,
    remarksFilter,
    sortField,
    sortOrder,
  ]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const toggleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(filtered.map((_, index) => index));
      return;
    }

    setSelected([]);
  };

  const toggleRow = (index) => {
    setSelected((prev) =>
      prev.includes(index)
        ? prev.filter((item) => item !== index)
        : [...prev, index]
    );
  };


  // Generic toggle for a multi-select checkbox filter (Stage, Status).
  const makeToggleHandler = (setter) => (value) => {
    setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const toggleStageFilter = makeToggleHandler(setStageFilter);
  const toggleStatusFilter = makeToggleHandler(setStatusFilter);

  const clearFilters = () => {
    setStageFilter([]);
    setStatusFilter([]);
    setCustomerNameFilter([]);
    setPresalesFilter([]);
    setBidManagerFilter([]);
    setStateFilter("");
    setCityFilter("");
    setAccountManagerFilter([]);
    setOemFilter("");
    setOemAcMgrFilter("");
    setProductFilter("");
    setAmountMin("");
    setAmountMax("");
    setExpectedClosureFrom("");
    setExpectedClosureTo("");
    setDrNumberFilter("");
    setDrExpiryFrom("");
    setDrExpiryTo("");
    setRemarksFilter("");
  };

  // Admin-only: bypass the "pick an Account Manager first" restriction and
  // show every opportunity at once.
  const handleViewAll = () => {
    setAccountManagerFilter([]);
    setViewAll((prev) => !prev);
  };

  const activeFilterCount =
    stageFilter.length +
    statusFilter.length +
    customerNameFilter.length +
    presalesFilter.length +
    bidManagerFilter.length +
    (stateFilter ? 1 : 0) +
    (cityFilter ? 1 : 0) +
    (oemFilter ? 1 : 0) +
    (oemAcMgrFilter ? 1 : 0) +
    (productFilter ? 1 : 0) +
    (amountMin !== "" ? 1 : 0) +
    (amountMax !== "" ? 1 : 0) +
    (expectedClosureFrom ? 1 : 0) +
    (expectedClosureTo ? 1 : 0) +
    (drNumberFilter ? 1 : 0) +
    (drExpiryFrom ? 1 : 0) +
    (drExpiryTo ? 1 : 0) +
    (remarksFilter ? 1 : 0);

  const handleExport = () => {
    const columns = [
      { label: "Opportunity ID", value: (l) => l.ld_id ?? l.id ?? "" },
      { label: "Customer Name", value: (l) => l.customerName || l.cust_name || "" },
      { label: "Account Manager", value: (l) => l.account_manager || "" },
      { label: "Pre-Sales", value: (l) => l.presales || "" },
      { label: "OEM", value: (l) => l.oem || l.ld_oem || "" },
      { label: "OEM A/C Mgr", value: (l) => l.oemAcMgr || l.ld_oem_ac_mgr || "" },
      { label: "Product", value: (l) => l.product || l.ld_product || "" },
      { label: "Stage", value: (l) => l.level || l.ld_level_id || "" },
      { label: "Status", value: (l) => l.status || "" },
      { label: "Amount", value: (l) => l.estimatedBudget ?? l.ld_estimated_budget ?? "" },
      { label: "Expected Closure", value: (l) => formatDate(l.expectedClosure || l.ld_expected_closure) },
      { label: "DR Number", value: (l) => l.drNumber || l.ld_dr_number || "" },
      { label: "DR Expiry", value: (l) => formatDate(l.drExpiry || l.ld_dr_expiry) },
      { label: "Remarks", value: (l) => l.statusRemark || l.ld_remarks || "" },
      { label: "OPF Raised", value: (l) => getOpfStatus(l) },
    ];

    if (filtered.length === 0) {
      showToast("No opportunities to export", "warning");
      return;
    }

    exportRowsToCSV(filtered, columns, `Opportunities_export_${new Date().toISOString().slice(0, 10)}.csv`);
    showToast("Opportunities exported to CSV", "success");
  };

  const navigateToLead = (leadId) => {
    navigate(`/leads/${leadId}`);
  };

  const isLeadClosed = (lead) =>
    String(lead.status || "").toLowerCase() === "won";

  const handleEditLead = (event, lead) => {
    event.stopPropagation();
    setActiveDropdownId(null);
    navigateToLead(lead.ld_id || lead.id);
  };

  const handleDeleteLead = async (event, lead) => {
    event.stopPropagation();
    setActiveDropdownId(null);
    const leadId = lead.ld_id || lead.id;
    const displayName = lead.customerName || lead.cust_name || `LD-${leadId}`;

    if (isLeadClosed(lead)) {
      showToast("Closed leads cannot be deleted.", "warning");
      return;
    }
    if (
      !(await confirm(
        `CRITICAL WARNING: Permanently delete "${displayName}" and all its history? This cannot be undone.`
      ))
    ) {
      return;
    }

    try {
      setUpdatingId(`delete-${leadId}`);
      const response = await api.delete(`/leads/${leadId}`);
      if (response.data?.success) {
        showToast(`${displayName} deleted.`, "success");
        setSelected([]);
        fetchLeads();
      } else {
        showToast(response.data?.message || "Error deleting opportunity.", "error");
      }
    } catch (error) {
      showToast(error.response?.data?.message || "Error deleting opportunity.", "error");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleRaiseOpf = (event, lead) => {
    event.stopPropagation();
    setActiveDropdownId(null);
    const leadId = lead.ld_id || lead.id;

    if (!isLeadClosed(lead)) {
      showToast("Lead must be marked Closed before raising an OPF.", "warning");
      return;
    }

    navigate(`/leads/${leadId}/raise-opf`, {
      state: {
        customerName: lead.customerName || lead.cust_name,
        address: lead.address || lead.cust_address,
        leadId: lead.leadId || `LD-${String(leadId).padStart(4, "0")}`,
      },
    });
  };

  const handleStatusChange = async (lead, newStatus) => {
    const leadId = lead.ld_id || lead.id;
    if (!newStatus || newStatus === (lead.status || "")) return;
    if (isLeadClosed(lead)) {
      showToast("This lead is marked as Won. Status is locked.", "warning");
      return;
    }
    try {
      setUpdatingId(`status-${leadId}`);
      const response = await api.patch(`/leads/${leadId}/stage`, { stage_name: newStatus });
      if (response.data?.success) {
        showToast(`Status updated to ${newStatus}`, "success");
        fetchLeads();
      }
    } catch (error) {
      showToast(error.response?.data?.message || "Error updating status.", "error");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleStageChange = async (lead, newStage) => {
    const leadId = lead.ld_id || lead.id;
    const currentStage = lead.level || lead.ld_level_id || "";
    if (!newStage || newStage === currentStage) return;
    try {
      setUpdatingId(`stage-${leadId}`);
      const response = await api.patch(`/leads/${leadId}/level`, { level_name: newStage });
      if (response.data?.success) {
        showToast(`Stage updated to ${newStage}`, "success");
        fetchLeads();
      }
    } catch (error) {
      showToast(error.response?.data?.message || "Error updating stage.", "error");
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="ml-[60px] min-h-screen bg-[#F1F5F9] p-3 sm:p-6 pt-16 overflow-x-hidden mt-12 md:h-[calc(100vh-3rem)] md:min-h-0 md:flex md:flex-col md:overflow-hidden">
      <Toast toast={toast} onClose={hideToast} />
      <ConfirmDialog confirmState={confirmState} onConfirm={handleConfirm} onCancel={handleCancel} />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-black text-slate-800">
            Opportunities
          </h1>

          <p className="mt-1 text-sm text-slate-500">
            Opportunities &gt; List | Total Opportunities: {filtered.length}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <div className="relative" ref={amMenuRef}>
              <button
                onClick={() => setAmMenuOpen((prev) => !prev)}
                className={`relative flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition ${
                  amMenuOpen || accountManagerFilter.length > 0
                    ? "border-blue-300 bg-blue-50 text-blue-700"
                    : "border-slate-200 bg-white hover:bg-slate-50"
                }`}
              >
                <Users size={14} />
                Account Manager
                {accountManagerFilter.length > 0 && (
                  <span className="flex items-center justify-center rounded-full bg-blue-600 text-white text-[10px] font-bold w-4.5 h-4.5 min-w-[18px] min-h-[18px] px-1">
                    {accountManagerFilter.length}
                  </span>
                )}
              </button>

              {amMenuOpen && (
                <div className="absolute right-0 top-[calc(100%+6px)] z-30 w-64 max-w-[90vw] rounded-xl border border-slate-200 bg-white shadow-lg p-3">
                  <MultiSelectFilterField
                    label="Account Manager"
                    placeholder="Type to search..."
                    options={accountManagerOptions}
                    selectedValues={accountManagerFilter}
                    onChange={(value) => {
                      setViewAll(false);
                      setAccountManagerFilter(value);
                    }}
                    onClear={() => setAccountManagerFilter([])}
                  />
                </div>
              )}
            </div>
          )}

          {isAdmin && (
            <button
              onClick={handleViewAll}
              className={`rounded-lg border px-3 py-2 text-sm font-medium transition ${
                viewAll
                  ? "border-blue-300 bg-blue-50 text-blue-700"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {viewAll ? "Hide All" : "View All"}
            </button>
          )}

          <div className="relative" ref={filterMenuRef}>
            <button
              onClick={() => setFilterMenuOpen((prev) => !prev)}
              className={`relative flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer transition ${
                filterMenuOpen || activeFilterCount > 0
                  ? "border-blue-300 bg-blue-50 text-blue-700"
                  : "border-slate-200 bg-white hover:bg-slate-50"
              }`}
            >
              <Filter size={14} />
              Filter
              {activeFilterCount > 0 && (
                <span className="flex items-center justify-center rounded-full bg-blue-600 text-white text-[10px] font-bold w-4.5 h-4.5 min-w-[18px] min-h-[18px] px-1">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {filterMenuOpen && (
              <div className="absolute right-0 top-[calc(100%+6px)] z-30 w-80 max-w-[90vw] max-h-[75vh] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg p-3">
                <div className="flex items-center justify-between mb-2 sticky -top-3 -mt-3 -mx-3 px-3 pt-3 pb-2 bg-white border-b border-slate-100 z-10">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
                    Filters
                  </p>
                  {activeFilterCount > 0 && (
                    <button
                      onClick={clearFilters}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-700 cursor-pointer"
                    >
                      Clear all
                    </button>
                  )}
                </div>

                {/* Customer Name */}
                <MultiSelectFilterField
                  label="Customer Name"
                  placeholder="Type to search..."
                  options={customerNameOptions}
                  selectedValues={customerNameFilter}
                  onChange={setCustomerNameFilter}
                />

                {/* Pre-Sales */}
                <MultiSelectFilterField
                  label="Pre-Sales"
                  placeholder="Type to search..."
                  options={presalesOptions}
                  selectedValues={presalesFilter}
                  onChange={setPresalesFilter}
                />

                {/* Bid Manager */}
                <MultiSelectFilterField
                  label="Bid Manager"
                  placeholder="Type to search..."
                  options={bidManagerOptions}
                  selectedValues={bidManagerFilter}
                  onChange={setBidManagerFilter}
                />

                {/* State */}
                <div className="mb-3">
                  <p className="mb-1.5 text-xs font-bold text-slate-600">State</p>
                  <input
                    type="text"
                    placeholder="Contains..."
                    value={stateFilter}
                    onChange={(event) => setStateFilter(event.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* City */}
                <div className="mb-3">
                  <p className="mb-1.5 text-xs font-bold text-slate-600">City</p>
                  <input
                    type="text"
                    placeholder="Contains..."
                    value={cityFilter}
                    onChange={(event) => setCityFilter(event.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* OEM */}
                <div className="mb-3">
                  <p className="mb-1.5 text-xs font-bold text-slate-600">OEM</p>
                  <input
                    type="text"
                    placeholder="Contains..."
                    value={oemFilter}
                    onChange={(event) => setOemFilter(event.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* OEM A/C Mgr */}
                <div className="mb-3">
                  <p className="mb-1.5 text-xs font-bold text-slate-600">OEM A/C Mgr</p>
                  <input
                    type="text"
                    placeholder="Contains..."
                    value={oemAcMgrFilter}
                    onChange={(event) => setOemAcMgrFilter(event.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Product */}
                <div className="mb-3">
                  <p className="mb-1.5 text-xs font-bold text-slate-600">Product</p>
                  <input
                    type="text"
                    placeholder="Contains..."
                    value={productFilter}
                    onChange={(event) => setProductFilter(event.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Stage */}
                <div className="mb-3">
                  <p className="mb-1.5 text-xs font-bold text-slate-600">Stage</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {stageOptions.length === 0 && (
                      <p className="text-xs text-slate-400">No stages available</p>
                    )}
                    {stageOptions.map((opt) => {
                      const value = opt.code_short_description;
                      const isChecked = stageFilter.includes(value);
                      return (
                        <label
                          key={opt.code_id}
                          className="flex items-center gap-2 rounded-md px-1.5 py-1 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer"
                        >
                          <span
                            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                              isChecked
                                ? "bg-blue-600 border-blue-600 text-white"
                                : "border-slate-300"
                            }`}
                          >
                            {isChecked && <Check size={12} />}
                          </span>
                          <input
                            type="checkbox"
                            className="hidden"
                            checked={isChecked}
                            onChange={() => toggleStageFilter(value)}
                          />
                          <span className="truncate">{value}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Status */}
                <div className="mb-3">
                  <p className="mb-1.5 text-xs font-bold text-slate-600">Status</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {statusOptions.length === 0 && (
                      <p className="text-xs text-slate-400">No statuses available</p>
                    )}
                    {statusOptions.map((opt) => {
                      const value = opt.code_short_description;
                      const isChecked = statusFilter.includes(value);
                      return (
                        <label
                          key={opt.code_id}
                          className="flex items-center gap-2 rounded-md px-1.5 py-1 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer"
                        >
                          <span
                            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                              isChecked
                                ? "bg-blue-600 border-blue-600 text-white"
                                : "border-slate-300"
                            }`}
                          >
                            {isChecked && <Check size={12} />}
                          </span>
                          <input
                            type="checkbox"
                            className="hidden"
                            checked={isChecked}
                            onChange={() => toggleStatusFilter(value)}
                          />
                          <span className="truncate">{value}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Amount range */}
                <div className="mb-3">
                  <p className="mb-1.5 text-xs font-bold text-slate-600">Amount</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      placeholder="Min"
                      value={amountMin}
                      onChange={(event) => setAmountMin(event.target.value)}
                      className="w-1/2 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-xs text-slate-400">to</span>
                    <input
                      type="number"
                      min="0"
                      placeholder="Max"
                      value={amountMax}
                      onChange={(event) => setAmountMax(event.target.value)}
                      className="w-1/2 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Expected Closure range */}
                <div className="mb-3">
                  <p className="mb-1.5 text-xs font-bold text-slate-600">Expected Closure</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={expectedClosureFrom}
                      onChange={(event) => setExpectedClosureFrom(event.target.value)}
                      className="w-1/2 rounded-lg border border-slate-200 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-xs text-slate-400">to</span>
                    <input
                      type="date"
                      value={expectedClosureTo}
                      onChange={(event) => setExpectedClosureTo(event.target.value)}
                      className="w-1/2 rounded-lg border border-slate-200 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* DR Number */}
                <div className="mb-3">
                  <p className="mb-1.5 text-xs font-bold text-slate-600">DR Number</p>
                  <input
                    type="text"
                    placeholder="Contains..."
                    value={drNumberFilter}
                    onChange={(event) => setDrNumberFilter(event.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* DR Expiry range */}
                <div className="mb-3">
                  <p className="mb-1.5 text-xs font-bold text-slate-600">DR Expiry</p>
                  <div className="flex items-center gap-2">
                    <input
                      type="date"
                      value={drExpiryFrom}
                      onChange={(event) => setDrExpiryFrom(event.target.value)}
                      className="w-1/2 rounded-lg border border-slate-200 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-xs text-slate-400">to</span>
                    <input
                      type="date"
                      value={drExpiryTo}
                      onChange={(event) => setDrExpiryTo(event.target.value)}
                      className="w-1/2 rounded-lg border border-slate-200 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Remarks */}
                <div>
                  <p className="mb-1.5 text-xs font-bold text-slate-600">Remarks</p>
                  <input
                    type="text"
                    placeholder="Contains..."
                    value={remarksFilter}
                    onChange={(event) => setRemarksFilter(event.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}
          </div>

          <button
            onClick={handleExport}
            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer"
          >
            <Download size={14} />
            Export
          </button>

          <button
            onClick={() => setOpen(true)}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 cursor-pointer"
          >
            + Add Opportunity
          </button>

          {open && (
            <AddLeadModal
              onClose={(successMessage) => {
                setOpen(false);
                fetchLeads();
                if (successMessage) showToast(successMessage, "success");
              }}
            />
          )}
        </div>
      </div>

      <div className="mb-4 rounded-lg border border-slate-200 bg-white p-3">
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            placeholder="Search by Opportunity ID, Customer or Account Manager..."
            className="w-full sm:flex-1 sm:min-w-[250px] rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />

          <button
            onClick={() => setActiveFilter("All")}
            className={`rounded-full px-4 py-2 text-sm font-medium transition cursor-pointer ${
              activeFilter === "All"
                ? "bg-blue-600 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            All Opportunities
          </button>

          {OPF_STATUSES.map((status) => (
            <button
              key={status}
              onClick={() => setActiveFilter(status)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition cursor-pointer ${
                activeFilter === status
                  ? "bg-blue-600 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-auto max-h-[70vh] md:max-h-none md:flex-1 md:min-h-0 flex flex-col">
        <table className="w-full min-w-[1400px] text-sm shrink-0 [&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10 [&_thead_th]:bg-slate-50 [&_thead_th]:shadow-[inset_0_-1px_0_#e2e8f0]">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 border-b border-slate-200">
            <tr>
              <th className="w-12 p-3 text-center">
                <input
                  type="checkbox"
                  onChange={toggleSelectAll}
                  checked={
                    filtered.length > 0 && selected.length === filtered.length
                  }
                  className="cursor-pointer"
                />
              </th>
              <th
                className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                onClick={() => handleSort("id")}
              >
                <span className="inline-flex items-center gap-1">
                  OPP ID
                  {sortField === "id" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                </span>
              </th>
              <th
                className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                onClick={() => handleSort("customerName")}
              >
                <span className="inline-flex items-center gap-1">
                  Customer Name
                  {sortField === "customerName" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                </span>
              </th>
              <th
                className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                onClick={() => handleSort("accountManager")}
              >
                <span className="inline-flex items-center gap-1">
                  Account Manager
                  {sortField === "accountManager" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                </span>
              </th>
              <th
                className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                onClick={() => handleSort("presales")}
              >
                <span className="inline-flex items-center gap-1">
                  Pre-Sales
                  {sortField === "presales" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                </span>
              </th>

              <th
                className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                onClick={() => handleSort("bid_manager")}
              >
                <span className="inline-flex items-center gap-1">
                  Bid Manager
                  {sortField === "bid_manager" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                </span>
              </th>

              <th
                className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                onClick={() => handleSort("oem")}
              >
                <span className="inline-flex items-center gap-1">
                  OEM
                  {sortField === "oem" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                </span>
              </th>
              <th
                className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                onClick={() => handleSort("oemAcMgr")}
              >
                <span className="inline-flex items-center gap-1">
                  OEM A/C Mgr
                  {sortField === "oemAcMgr" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                </span>
              </th>
              <th
                className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                onClick={() => handleSort("product")}
              >
                <span className="inline-flex items-center gap-1">
                  Product
                  {sortField === "product" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                </span>
              </th>
              <th
                className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                onClick={() => handleSort("level")}
              >
                <span className="inline-flex items-center gap-1">
                  Stages
                  {sortField === "level" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                </span>
              </th>
              <th
                className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                onClick={() => handleSort("status")}
              >
                <span className="inline-flex items-center gap-1">
                  Status
                  {sortField === "status" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                </span>
              </th>
              <th
                className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                onClick={() => handleSort("amount")}
              >
                <span className="inline-flex items-center gap-1">
                  Amount
                  {sortField === "amount" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                </span>
              </th>
              <th
                className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                onClick={() => handleSort("expectedClosure")}
              >
                <span className="inline-flex items-center gap-1">
                  Expected Closure
                  {sortField === "expectedClosure" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                </span>
              </th>
              <th
                className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                onClick={() => handleSort("drNumber")}
              >
                <span className="inline-flex items-center gap-1">
                  DR Number
                  {sortField === "drNumber" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                </span>
              </th>
              <th
                className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                onClick={() => handleSort("drExpiry")}
              >
                <span className="inline-flex items-center gap-1">
                  DR Expiry
                  {sortField === "drExpiry" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                </span>
              </th>
              <th
                className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                onClick={() => handleSort("remarks")}
              >
                <span className="inline-flex items-center gap-1">
                  Remarks
                  {sortField === "remarks" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                </span>
              </th>
              <th
                className="p-3 text-left cursor-pointer select-none hover:text-slate-700"
                onClick={() => handleSort("opfRaised")}
              >
                <span className="inline-flex items-center gap-1">
                  OPF Status
                  {sortField === "opfRaised" ? (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="text-slate-300" />}
                </span>
              </th>
              <th className="p-3 text-center">Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? null : paginatedLeads.map(({ lead, index }) => (
              <tr
                key={lead.ld_id || lead.id || index}
                className="border-b border-slate-100 transition hover:bg-slate-50/80"
              >
                <td
                  className="p-3 text-center"
                  onClick={(event) => event.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(index)}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => toggleRow(index)}
                    className="cursor-pointer"
                  />
                </td>

                <td
                  className="cursor-pointer p-3 text-slate-700"
                  onClick={() => navigateToLead(lead.ld_id || lead.id)}
                >
                  <span className="font-mono font-semibold text-blue-600">
                    {lead.ld_id}
                  </span>
                </td>

                <td
                  className="cursor-pointer p-3 max-w-[160px]"
                  onClick={() => navigateToLead(lead.ld_id || lead.id)}
                  title={lead.customerName || lead.cust_name || ""}
                >
                  <div className="flex items-center gap-3">
                    

                    <div className="font-semibold text-slate-800 truncate">
                      {lead.customerName || lead.cust_name || "-"}
                    </div>
                  </div>
                </td>

                <td
                  className="cursor-pointer p-3 text-slate-700 max-w-[140px]"
                  onClick={() => navigateToLead(lead.ld_id || lead.id)}
                  title={lead.account_manager || ""}
                >
                  <div className="truncate">{lead.account_manager || "-"}</div>
                  {lead.account_manager_email && (
                    <div className="text-xs text-slate-400 truncate">{lead.account_manager_email}</div>
                  )}
                </td>

                <td
                  className="cursor-pointer p-3 text-slate-700 max-w-[140px]"
                  onClick={() => navigateToLead(lead.ld_id || lead.id)}
                  title={lead.presales || ""}
                >
                  <div className="truncate">{lead.presales || "-"}</div>
                  {lead.presales_email && (
                    <div className="text-xs text-slate-400 truncate">{lead.presales_email}</div>
                  )}
                </td>

                <td
                  className="cursor-pointer p-3 text-slate-700 max-w-[140px]"
                  onClick={() => navigateToLead(lead.ld_id || lead.id)}
                  title={lead.bid_manager || ""}
                >
                  <div className="truncate">{lead.bid_manager || "-"}</div>
                  {lead.bid_manager_email && (
                    <div className="text-xs text-slate-400 truncate">{lead.bid_manager_email}</div>
                  )}
                </td>

                <td
                  className="cursor-pointer p-3 text-slate-700 max-w-[120px] truncate"
                  onClick={() => navigateToLead(lead.ld_id || lead.id)}
                  title={lead.oem || lead.ld_oem || ""}
                >
                  {lead.oem || lead.ld_oem || "-"}
                </td>

                <td
                  className="cursor-pointer p-3 text-slate-700 max-w-[120px] truncate"
                  onClick={() => navigateToLead(lead.ld_id || lead.id)}
                  title={lead.oemAcMgr || lead.ld_oem_ac_mgr || ""}
                >
                  {lead.oemAcMgr || lead.ld_oem_ac_mgr || "-"}
                </td>

                <td
                  className="cursor-pointer p-3 text-slate-700 max-w-[130px] truncate"
                  onClick={() => navigateToLead(lead.ld_id || lead.id)}
                  title={lead.product || lead.ld_product || ""}
                >
                  {lead.product || lead.ld_product || "-"}
                </td>

                <td
                  className="p-3"
                  onClick={(event) => event.stopPropagation()}
                >
                  <select
                    value={lead.level || lead.ld_level_id || ""}
                    onChange={(event) => handleStageChange(lead, event.target.value)}
                    disabled={updatingId === `stage-${lead.ld_id || lead.id}`}
                    className="cursor-pointer rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {!stageOptions.some((opt) => opt.code_short_description === (lead.level || lead.ld_level_id)) && (
                      <option value={lead.level || lead.ld_level_id || ""}>
                        {lead.level || lead.ld_level_id || "-"}
                      </option>
                    )}
                    {stageOptions.map((opt) => (
                      <option key={opt.code_id} value={opt.code_short_description}>
                        {opt.code_short_description}
                      </option>
                    ))}
                  </select>
                </td>

                <td
                  className="p-3"
                  onClick={(event) => event.stopPropagation()}
                >
                  <select
                    value={lead.status || ""}
                    onChange={(event) => handleStatusChange(lead, event.target.value)}
                    disabled={updatingId === `status-${lead.ld_id || lead.id}` || isLeadClosed(lead)}
                    title={isLeadClosed(lead) ? "Status is locked once a lead is marked Won." : ""}
                    className={`cursor-pointer rounded-full border-0 px-2.5 py-1 text-xs font-bold focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${getStatusClasses(lead.status)}`}
                  >
                    {!statusOptions.some((opt) => opt.code_short_description === lead.status) && (
                      <option value={lead.status || ""}>{lead.status || "New"}</option>
                    )}
                    {statusOptions.map((opt) => (
                      <option key={opt.code_id} value={opt.code_short_description}>
                        {opt.code_short_description}
                      </option>
                    ))}
                  </select>
                </td>

                <td
                  className="cursor-pointer p-3 text-slate-700 font-medium"
                  onClick={() => navigateToLead(lead.ld_id || lead.id)}
                >
                  ₹{formatIndianCurrency(lead.estimatedBudget ?? lead.ld_estimated_budget)  || 0}
                </td>

                <td
                  className="cursor-pointer p-3 text-slate-500 text-xs"
                  onClick={() => navigateToLead(lead.ld_id || lead.id)}
                >
                  {formatDate(lead.expectedClosure || lead.ld_expected_closure)}
                </td>

                <td
                  className="cursor-pointer p-3 text-slate-500 font-mono text-xs"
                  onClick={() => navigateToLead(lead.ld_id || lead.id)}
                >
                  {lead.drNumber || lead.ld_dr_number || "-"}
                </td>

                <td
                  className="cursor-pointer p-3 text-slate-500 text-xs"
                  onClick={() => navigateToLead(lead.ld_id || lead.id)}
                >
                  {formatDate(lead.drExpiry || lead.ld_dr_expiry)}
                </td>

                <td
                  className="cursor-pointer p-3 text-slate-500 max-w-[90px] truncate"
                  onClick={() => navigateToLead(lead.ld_id || lead.id)}
                  title={lead.statusRemark || lead.ld_remarks || ""}
                >
                  {lead.statusRemark || lead.ld_remarks || "-"}
                </td>

                <td
                  className="cursor-pointer p-3"
                  onClick={() => navigateToLead(lead.ld_id || lead.id)}
                >
                  <span
                    className={`rounded-full px-2.5 py-1 text-xs font-bold whitespace-nowrap ${
                      OPF_STATUS_CLASSES[getOpfStatus(lead)] || OPF_STATUS_CLASSES["Not Raised"]
                    }`}
                  >
                    {getOpfStatus(lead)}
                  </span>
                </td>

                <td
                  className="p-3 text-center"
                  onClick={(event) => event.stopPropagation()}
                >
                  <div
                    className="relative inline-block text-left"
                    ref={activeDropdownId === index ? dropdownRef : null}
                  >
                    <button
                      type="button"
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 cursor-pointer"
                      onClick={(event) => {
                        event.stopPropagation();
                        setActiveDropdownId(
                          activeDropdownId === index ? null : index
                        );
                      }}
                    >
                      <MoreHorizontal size={16} />
                    </button>

                    {activeDropdownId === index && (
                      <div
                        className={`absolute right-0 z-50 w-40 rounded-xl border border-slate-200 bg-white py-1.5 shadow-xl ${
                          index >= filtered.length - 2
                            ? "bottom-full mb-1.5"
                            : "top-full mt-1.5"
                        }`}
                      >
                        <button
                          type="button"
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 text-left"
                          onClick={(event) => handleEditLead(event, lead)}
                        >
                        <Pencil size={13} className="text-blue-600" />
                          View Details
                        </button>

                        {/* <div className="my-1 h-px bg-slate-100"></div>

                        <button
                          type="button"
                          disabled={updatingId === `delete-${lead.ld_id || lead.id}`}
                          className="w-full px-3 py-2 text-left text-xs font-semibold text-red-600 hover:bg-red-50 cursor-pointer disabled:opacity-50 disabled:cursor-wait"
                          onClick={(event) => handleDeleteLead(event, lead)}
                        >
                          Delete Opportunity
                        </button> */}

                        {/* <div className="my-1 h-px bg-slate-100"></div> */}

                        {/* <button
                          type="button"
                          disabled={!isLeadClosed(lead)}
                          title={
                            isLeadClosed(lead)
                              ? ""
                              : "Lead must be Closed before an OPF can be raised"
                          }
                          className="w-full px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                          onClick={(event) => handleRaiseOpf(event, lead)}
                        >
                          Generate OPF
                        </button> */}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {loading && (
          <div className="flex flex-1 items-center justify-center py-12 text-sm text-slate-500">
            Loading opportunities...
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="flex flex-1 items-center justify-center py-12 text-center text-sm font-medium text-slate-400 bg-slate-50/50 px-4">
            {isAdminBlankState
              ? "Select an Account Manager above (or click View All) to see opportunities."
              : "No Opportunities Found"}
          </div>
        )}
      </div>

      {/* PAGINATION */}
      {!loading && filtered.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 px-2 shrink-0">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span className="font-medium">Rows per page:</span>
            <select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="cursor-pointer rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              aria-label="Rows per page"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className="inline-flex items-center gap-3 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 shadow-sm">
            <span className="text-slate-400">
              {(currentPage - 1) * itemsPerPage + 1}-
              {Math.min(currentPage * itemsPerPage, filtered.length)} of {filtered.length}
            </span>
            <div className="h-4 w-px bg-slate-200" />
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 transition cursor-pointer"
              aria-label="Previous page"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="font-medium text-slate-700">
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 transition cursor-pointer"
              aria-label="Next page"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* SELECTION DRAWER */}
      {drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          className="fixed inset-0 z-40 bg-black/30"
        />
      )}

      {/* <div
        className={`fixed right-0 top-0 z-50 h-full w-[320px] transform bg-white shadow-xl transition-transform duration-300 ${
          selected.length > 0 ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="border-b p-4 font-semibold text-slate-800">
          {selected.length > 1 ? "Bulk Summary" : "Lead Details"}
        </div>

        <div className="space-y-4 p-4">
          {selected.length > 1 && (
            <>
              {selectedLeads.map((lead, index) => (
                <div key={index} className="text-sm text-slate-700 font-medium">- {lead.customerName || lead.cust_name}</div>
              ))}

              <button className="mt-4 w-full rounded-lg bg-blue-600 py-2 text-white text-sm font-medium hover:bg-blue-700 transition cursor-pointer">
                Apply Bulk Update
              </button>
            </>
          )}

          {selected.length === 1 && selectedLeads[0] && (
            <>
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-600 text-white font-semibold uppercase">
                  {(selectedLeads[0].customerName || selectedLeads[0].cust_name || "LD").substring(0, 2)}
                </div>

                <div>
                  <div className="font-semibold text-slate-800">
                    {selectedLeads[0].customerName || selectedLeads[0].cust_name}
                  </div>

                  <div className="text-sm text-slate-500 capitalize">
                    {selectedLeads[0].status || "New"}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex gap-4 text-slate-600">
                <Phone className="cursor-pointer hover:text-blue-600 transition" size={18} />
                <MessageCircle className="cursor-pointer hover:text-blue-600 transition" size={18} />
              </div>
            </>
          )}
        </div>
      </div> */}
    </div>
  );
}